import proj4 from "proj4";

/**
 * Centralny moduł definicji układów współrzędnych używanych w aplikacji.
 *
 * UWAGA MERYTORYCZNA:
 * - EPSG:2180  = PL-1992 / CS92 (jednopasowy, południk osiowy 19°E) — używany przez ULDK
 *                jako domyślny format zwracanych współrzędnych (srid=2180).
 * - EPSG:2176  = PL-2000 strefa 5 (lon_0 = 15°E) — zachodnia Polska (Szczecin, Zielona Góra).
 * - EPSG:2177  = PL-2000 strefa 6 (lon_0 = 18°E) — centralno-zachodnia (Poznań, Wrocław, Katowice).
 * - EPSG:2178  = PL-2000 strefa 7 (lon_0 = 21°E) — centralno-wschodnia (Warszawa, Łódź, Lublin).
 * - EPSG:2179  = PL-2000 strefa 8 (lon_0 = 24°E) — wschodnia Polska (Białystok, Rzeszów).
 *
 * 2180 to NIE jest to samo co dowolna strefa 2000 — różne x_0/y_0/południk osiowy.
 * Strefy 2176–2179 nie są zamienne między sobą — współrzędne z jednej strefy nie da się
 * poprawnie wyświetlić w drugiej bez reprojekcji (granice stref +/- 1.5° od lon_0).
 *
 * Wszystkie transformacje w kodzie aplikacji MUSZĄ używać stałych `CRS_*` zamiast literałów
 * "EPSG:...", żeby literówka o jedną cyfrę nie spowodowała ukrytego błędu merytorycznego.
 */

export const CRS_WGS84 = "EPSG:4326";
export const CRS_WEB_MERCATOR = "EPSG:3857";
export const CRS_PL1992 = "EPSG:2180";
export const CRS_PL2000_Z5 = "EPSG:2176";
export const CRS_PL2000_Z6 = "EPSG:2177";
export const CRS_PL2000_Z7 = "EPSG:2178";
export const CRS_PL2000_Z8 = "EPSG:2179";

export type PL2000Crs =
  | typeof CRS_PL2000_Z5
  | typeof CRS_PL2000_Z6
  | typeof CRS_PL2000_Z7
  | typeof CRS_PL2000_Z8;

/** Domyślny CRS widoku mapy, jeżeli nie znamy jeszcze lokalizacji użytkownika. */
export const DEFAULT_MAP_CRS: PL2000Crs = CRS_PL2000_Z7;

/** CRS zwracany domyślnie przez usługę ULDK Geoportalu. */
export const ULDK_CRS = CRS_PL1992;

// Definicje proj4 (+ellps=GRS80, zgodne z ETRS89 — tak są zdefiniowane 2180/2176–2179).
const DEF_PL1992 =
  "+proj=tmerc +lat_0=0 +lon_0=19 +k=0.9993 +x_0=500000 +y_0=-5300000 +ellps=GRS80 +units=m +no_defs";

// W definicjach stref PL-2000 prefiks 5/6/7/8 w x_0 koduje numer strefy (5500000, 6500000,
// 7500000, 8500000). k=0.999923, y_0=0, lon_0 co 3° od 15°E.
const DEF_PL2000_Z5 =
  "+proj=tmerc +lat_0=0 +lon_0=15 +k=0.999923 +x_0=5500000 +y_0=0 +ellps=GRS80 +units=m +no_defs";
const DEF_PL2000_Z6 =
  "+proj=tmerc +lat_0=0 +lon_0=18 +k=0.999923 +x_0=6500000 +y_0=0 +ellps=GRS80 +units=m +no_defs";
const DEF_PL2000_Z7 =
  "+proj=tmerc +lat_0=0 +lon_0=21 +k=0.999923 +x_0=7500000 +y_0=0 +ellps=GRS80 +units=m +no_defs";
const DEF_PL2000_Z8 =
  "+proj=tmerc +lat_0=0 +lon_0=24 +k=0.999923 +x_0=8500000 +y_0=0 +ellps=GRS80 +units=m +no_defs";

let proj4Initialized = false;
let olRegistered = false;

/**
 * Rejestruje definicje proj4 dla 2180 oraz wszystkich stref PL-2000 (2176–2179).
 * Synchroniczne, idempotentne. Musi być wywołane PRZED pierwszym użyciem proj4(...)
 * i przed `register(proj4)` w OpenLayers. Wołane z main.tsx.
 */
export function initProjectionDefinitions(): void {
  if (proj4Initialized) return;
  proj4.defs(CRS_PL1992, DEF_PL1992);
  proj4.defs(CRS_PL2000_Z5, DEF_PL2000_Z5);
  proj4.defs(CRS_PL2000_Z6, DEF_PL2000_Z6);
  proj4.defs(CRS_PL2000_Z7, DEF_PL2000_Z7);
  proj4.defs(CRS_PL2000_Z8, DEF_PL2000_Z8);
  proj4Initialized = true;

  if (import.meta.env?.DEV) {
    runProjectionSanityCheck();
  }
}

/**
 * Rejestruje proj4 w OpenLayers oraz ustawia extenty projekcji potrzebne do
 * reprojekcji raster-tile (WMS z GUGIK serwuje w 2180 — klient reprojektuje
 * do widoku PL-2000). Bez extentu OL nie wie, jak zbudować siatkę kafli i
 * reprojekcja nie działa.
 * Używa dynamicznego importu `ol/proj/proj4`, żeby nie wciągać OL do grafu
 * modułów synchronicznie (na tej gałęzi OL jest ładowany lazy).
 */
export async function registerProj4WithOpenLayers(): Promise<void> {
  if (olRegistered) return;
  initProjectionDefinitions();
  const [{ register }, { get: getProjection }] = await Promise.all([
    import("ol/proj/proj4"),
    import("ol/proj"),
  ]);
  register(proj4);

  // OŚ AXIS ORDER — kluczowe dla parsowania WMTS Capabilities GUGIK.
  //
  // Po `register(proj4)` OL ustawia każdej projekcji `axisOrientation='enu'`
  // (east-north-up) — bo proj4 domyślnie tak transformuje. ALE oficjalna
  // definicja EPSG dla PL-1992 (2180) i wszystkich stref PL-2000 (2176-2179)
  // ma axis order `(north, east)`, czyli pary współrzędnych w XML/WMTS/WFS
  // GUGIK są zapisywane jako `Y X`, nie `X Y`.
  //
  // Konkretnie GUGIK WMTS GetCapabilities zawiera np.:
  //     <TopLeftCorner>850000.0 100000.0</TopLeftCorner>
  // co znaczy `Y=850000, X=100000` (top-left = NW Polski). Bez tej korekty
  // OL czyta to jako `[X=850000, Y=100000]` (swap) i umieszcza origin
  // tileGrida na granicy z Białorusią. Dla widoku w okolicy Warszawy/
  // Krakowa/Nowego Sącza wszystkie wyliczone TileCol/Row wychodzą ujemne —
  // OL nie generuje żadnych requestów GetTile, mapa pozostaje pusta
  // (objaw na prod 2026-04-13).
  //
  // OL nie udostępnia publicznego settera `setAxisOrientation()` (axisOrientation
  // jest read-only po konstrukcji projekcji). Modyfikujemy private property
  // `axisOrientation_` bezpośrednio — to hack, ale jedyna alternatywa to
  // re-rejestracja projekcji (niebezpieczne, mogłoby zerwać proj4 transforms
  // dodane przez `register(proj4)` powyżej).
  //
  // Zmiana wpływa wyłącznie na parsowanie zewnętrznych źródeł (Capabilities,
  // WMS 1.3.0 BBOX, WFS GML axis-aware). Wewnętrzny stan OL (extenty,
  // `fromLonLat`, koordynaty kursora w `evt.coordinate`) zostaje w `[X, Y]` —
  // bo proj4 transform w obie strony używa `enu`. Dlatego pasek statusu
  // nadal pokazuje x: easting, y: northing bez swapu.
  //
  // WMS używamy w wersji 1.1.1 (działki KIEG, fallback ortho) — 1.1.1 zawsze
  // wysyła BBOX w east-north niezależnie od axis order projekcji, więc
  // ta zmiana NIE wpływa na żądania WMS GetMap.
  const forceAxisOrientationNEU = (code: string) => {
    const p = getProjection(code);
    if (p) (p as unknown as { axisOrientation_: string }).axisOrientation_ = "neu";
  };
  forceAxisOrientationNEU(CRS_PL1992);
  forceAxisOrientationNEU(CRS_PL2000_Z5);
  forceAxisOrientationNEU(CRS_PL2000_Z6);
  forceAxisOrientationNEU(CRS_PL2000_Z7);
  forceAxisOrientationNEU(CRS_PL2000_Z8);

  // Extenty użyteczne dla terenu Polski. Użyte przez OL do siatki kafli
  // podczas reprojekcji rastra. Wartości są z dużym zapasem (nie muszą być
  // dokładnymi granicami kraju — OL przyjmie ramkę i przytnie kafle w obrębie).
  // UWAGA: extent jest [minX, minY, maxX, maxY] w internal OL order
  // (zawsze east-north), niezależnie od `axisOrientation` ustawionego wyżej.
  getProjection(CRS_PL1992)?.setExtent([140000, 140000, 880000, 870000]);
  getProjection(CRS_PL2000_Z5)?.setExtent([5100000, 5200000, 5900000, 7500000]);
  getProjection(CRS_PL2000_Z6)?.setExtent([6100000, 5200000, 6900000, 7500000]);
  getProjection(CRS_PL2000_Z7)?.setExtent([7100000, 5200000, 7900000, 7500000]);
  getProjection(CRS_PL2000_Z8)?.setExtent([8100000, 5200000, 8900000, 7500000]);

  olRegistered = true;
}

/** Jednolity dostęp do proj4 dla miejsc, które nie chcą importować go bezpośrednio. */
export function getProj4(): typeof proj4 {
  initProjectionDefinitions();
  return proj4;
}

/**
 * Wybiera właściwą strefę PL-2000 na podstawie długości geograficznej (WGS84).
 * Granice stref przyjmuję symetrycznie co 3° wokół południków osiowych 15/18/21/24:
 *   - lng < 16.5°E   → strefa 5 (EPSG:2176)
 *   - 16.5° … 19.5°  → strefa 6 (EPSG:2177)
 *   - 19.5° … 22.5°  → strefa 7 (EPSG:2178)
 *   - lng ≥ 22.5°E   → strefa 8 (EPSG:2179)
 * Dla lokalizacji spoza Polski (lng poza 14..25) i tak wybierze najbliższą strefę —
 * decyzja akceptowalna, bo backend/ULDK obsługują tylko obszar PL.
 */
export function pickPL2000ZoneByLongitude(lng: number): PL2000Crs {
  if (lng < 16.5) return CRS_PL2000_Z5;
  if (lng < 19.5) return CRS_PL2000_Z6;
  if (lng < 22.5) return CRS_PL2000_Z7;
  return CRS_PL2000_Z8;
}

/**
 * Sanity check definicji projekcji w trybie dev. Porównuje wynik transformacji
 * punktu kontrolnego (Pałac Kultury, Warszawa) do oczekiwanych wartości. Jeżeli
 * któraś definicja jest błędna (np. zamienione parametry 2178↔2180), log w
 * konsoli wskaże to wprost, zanim obrazek „bez błędu, ale w złym miejscu" trafi
 * na produkcję.
 */
function runProjectionSanityCheck(): void {
  try {
    // Pałac Kultury i Nauki, Warszawa, WGS84
    const [lng, lat] = [21.00667, 52.23193];

    const [x2180, y2180] = proj4(CRS_WGS84, CRS_PL1992, [lng, lat]);
    const [x2178, y2178] = proj4(CRS_WGS84, CRS_PL2000_Z7, [lng, lat]);

    // Oczekiwane rzędy wielkości:
    // 2180: x ≈ 639 000, y ≈ 486 900
    // 2178: x ≈ 7 500 500, y ≈ 5 789 500
    const ok2180 = x2180 > 600_000 && x2180 < 700_000 && y2180 > 400_000 && y2180 < 550_000;
    const ok2178 = x2178 > 7_400_000 && x2178 < 7_600_000 && y2178 > 5_700_000 && y2178 < 5_850_000;

    // eslint-disable-next-line no-console
    console.info(
      "[projections] sanity check PKiN:",
      { EPSG_2180: [x2180, y2180], EPSG_2178: [x2178, y2178] },
      ok2180 && ok2178 ? "OK" : "FAIL — sprawdź definicje proj4.defs",
    );
    if (!ok2180 || !ok2178) {
      console.warn(
        "[projections] Transformacja punktu kontrolnego poza oczekiwanym zakresem. " +
          "Najczęstsza przyczyna: zamienione parametry w proj4.defs między 2178 a 2180.",
      );
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[projections] sanity check nie powiódł się:", err);
  }
}
